/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import uuid from 'uuid'
import { ATTACHMENT_TYPE, FROM_VIEW_TYPE } from '@v2/constants/common'
import { Schema$UserComment } from '@interface/comment'
import useRootStore from '@store/useRootStore'
import { AdvEditor, AdvEditorData } from '@components/AdvEditor'
import _ from 'lodash'
import { AdvComments } from '@components/AdvComment/index.style'
import classNames from 'classnames'

interface AdvCommentEditorProps {
  fromViewType: FROM_VIEW_TYPE
  commentProp?: Schema$UserComment
  callbackCreated?: (comment: Schema$UserComment) => void
  callbackUpdated?: (comment: Schema$UserComment) => void
  placeholder?: string
  callbackClose?: () => void
  callbackOpen?: () => void
  isFocus?: boolean
  showBettingHistory?: boolean
  singleMode?: boolean
  className?: string
}

export const AdvCommentEditor: React.FC<AdvCommentEditorProps> = observer((props) => {
  const { commentStore, authStore} = useRootStore()
  const {
    fromViewType, commentProp,
    placeholder, isFocus = true,
    showBettingHistory = true, singleMode = false,
    className,
    callbackClose, callbackOpen, callbackCreated, callbackUpdated,
  } = props

  const editorData: AdvEditorData = {
    isNew: _.isEmpty(_.trim(commentProp.id)),
    content: commentProp?.content,
    filteredContent: commentProp?.filteredContent,
    attachmentType: commentProp?.attachmentType,
    attachment: commentProp?.attachment,
    attachmentDetail: commentProp?.attachmentDetail,
  }

  const onSaveComment = async (editorData: AdvEditorData): Promise<boolean> => {
    if (commentProp.id) {
      const modifiedComment = {
        ...commentProp,
        content: _.trim(editorData.content),
        attachmentType: editorData.attachmentType as ATTACHMENT_TYPE,
        attachment: editorData.attachment,
        attachmentDetail: editorData.attachmentDetail,
      }
      const updatedComment = await commentStore.updateComment(modifiedComment)
      if (callbackUpdated && !_.isNil(updatedComment)) {
        callbackUpdated(updatedComment)
      }
      return !_.isNil(updatedComment)
    }
    const comment = {
      targetId: commentProp.targetId,
      commentType: commentProp.commentType,
      content: _.trim(editorData.content),
      attachmentType: editorData.attachmentType as ATTACHMENT_TYPE,
      attachment: editorData.attachment,
      attachmentDetail: editorData.attachmentDetail,
      commentDepth: commentProp.commentDepth ?? 0, // depth 가 1 이상은 답글 이다.
      replyToCommentId: commentProp.replyToCommentId,
      replyToUserNick: commentProp.replyToUserNick,
      rootCommentId: commentProp.rootCommentId,
      replyToCommentIdPath: commentProp.replyToCommentIdPath,
      commenter: {
        userPromiseId: authStore.userInfo.id,
      },
    } as Schema$UserComment

    const createdComment = await commentStore.createComment(comment)
    if (callbackCreated && !_.isNil(createdComment)) {
      callbackCreated(createdComment)
    }
    return !_.isNil(createdComment)
  }

  const editorClassName = classNames('', {
    [className]: !_.isEmpty(className),
  })

  const editorElement = (
    <AdvEditor
      key={`adv-comment-inner-editor_${fromViewType}_${commentProp.id || uuid.v4()}`}
      className={editorClassName}
      editorData={editorData}
      isFocus={isFocus}
      placeholder={placeholder || '댓글을 남겨보세요'}
      showBettingHistory={showBettingHistory}
      callbackSave={onSaveComment}
      fromViewType={fromViewType}
      callbackClose={callbackClose}
      callbackOpen={callbackOpen}
    />
  )

  if (singleMode) {
    return <AdvComments>{editorElement}</AdvComments>
  }

  return <Fragment>{editorElement}</Fragment>
})
